import { useEffect, useRef, useState } from "react";
import { collectNetworkMetrics } from "./networkVerify.ts";

type NetStatus = "ok" | "unstable" | "offline";

type Options = {
  pingUrl?: string;
  intervalMs?: number;

  // Regras de instabilidade (ajuste como quiser)
  unstableLatencyMs?: number;   // acima disso já é instável
  unstableJitterMs?: number;    // variação grande entre pings
  failToOffline?: number;       // quantas falhas seguidas viram offline
};

export function useInternetStatus(options: Options = {}) {
  const {
    pingUrl = "/ping",
    intervalMs = 4000,
    unstableLatencyMs = 900,
    unstableJitterMs = 350,
    failToOffline = 2,
  } = options;

  const [status, setStatus] = useState<NetStatus>("ok");
  const [lastMetrics, setLastMetrics] = useState<any>(null);

  const lastLatencyRef = useRef<number | null>(null);
  const failStreakRef = useRef(0);
  const timerRef = useRef<number | null>(null);

  const evaluate = (m: any) => {
    // 1) Offline “duro”
    if (m?.online === false) {
      failStreakRef.current = 0;
      lastLatencyRef.current = null;
      return "offline" as NetStatus;
    }

    // 2) Ping falhou
    if (m?.ping_ok === false) {
      failStreakRef.current += 1;

      if (failStreakRef.current >= failToOffline) return "offline";
      return "unstable";
    }

    // Ping ok
    failStreakRef.current = 0;

    const latency =
      typeof m?.ping_latencyMs === "number" ? m.ping_latencyMs : null;

    // 3) Se não tenho latency, não consigo dizer instável -> assume ok
    if (latency == null) return "ok";

    const last = lastLatencyRef.current;
    lastLatencyRef.current = latency;

    const jitter = last == null ? 0 : Math.abs(latency - last);

    // Regras de instabilidade
    if (latency >= unstableLatencyMs) return "unstable";
    if (jitter >= unstableJitterMs) return "unstable";

    return "ok";
  };

  const tick = async () => {
    try {
      const m = await collectNetworkMetrics(pingUrl);
      setLastMetrics(m);

      const next = evaluate(m);
      setStatus(next);
    } catch {
      // se falhar geral, trata como instável (ou offline se quiser)
      setStatus("unstable");
    }
  };

  useEffect(() => {
    const start = () => {
      tick(); // roda já
      if (timerRef.current) window.clearInterval(timerRef.current);
      timerRef.current = window.setInterval(tick, intervalMs);
    };

    const stop = () => {
      if (timerRef.current) window.clearInterval(timerRef.current);
      timerRef.current = null;
    };

    const onOnline = () => start();
    const onOffline = () => setStatus("offline");
    const onVis = () => {
      // não fica pingando em background
      if (document.visibilityState === "hidden") stop();
      else start();
    };

    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    document.addEventListener("visibilitychange", onVis);

    start();

    return () => {
      stop();
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
      document.removeEventListener("visibilitychange", onVis);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pingUrl, intervalMs]);

  return { status, lastMetrics };
}
